// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module PostHogProvider
 * @package @geenius/analytics
 * @description Implements the shared PostHog provider using the optional
 * `posthog-js` client. The provider lazily initializes PostHog and exposes the
 * feature-flag helpers needed by analytics-driven product surfaces.
 */

import type { AnalyticsEvent, PageView } from "../types";
import {
  type AnalyticsProviderConfig,
  BaseAnalyticsProvider,
  type UserTraits,
} from "./interface";

let importOptionalPeer = async <T>(
  specifier: string,
): Promise<{ default: T }> =>
  (await import(/* @vite-ignore */ specifier)) as { default: T };

/**
 * PostHog analytics provider backed by the optional browser tracker client.
 */
export class PostHogProvider extends BaseAnalyticsProvider {
  readonly name = "posthog" as const;
  private posthog: PostHogClient | null = null;

  /**
   * Stores the PostHog configuration for later lazy client initialization.
   *
   * @param config Provider configuration containing the PostHog project key.
   * @returns Nothing.
   */
  init(config: AnalyticsProviderConfig): void {
    super.init(config);
  }

  private async getClient(): Promise<PostHogClient> {
    if (!this.posthog) {
      const config = this.getRequiredConfig();
      const posthogModule =
        await importOptionalPeer<PostHogClient>("posthog-js");
      this.posthog = posthogModule.default;
      this.posthog.init(config.apiKey, {
        api_host: config.host || "https://us.i.posthog.com",
        // The shared tracker owns page and click dispatch so provider-level
        // autocapture does not duplicate unsanitized events.
        autocapture: false,
        capture_pageview: false,
        respect_dnt: config.respectDnt ?? true,
      });
    }
    return this.posthog;
  }

  /**
   * Sends a shared analytics event to PostHog.
   *
   * @param event Shared analytics event payload.
   * @returns Nothing.
   */
  async trackEvent(event: AnalyticsEvent): Promise<void> {
    const client = await this.getClient();
    client.capture(event.name, {
      ...event.properties,
      $current_url: event.url,
      $referrer: event.referrer,
      event_type: event.type,
    });
  }

  /**
   * Sends a page-view payload to PostHog.
   *
   * @param pageView Shared page-view payload.
   * @returns Nothing.
   */
  async trackPageView(pageView: PageView): Promise<void> {
    const client = await this.getClient();
    client.capture("$pageview", {
      ...pageView.properties,
      $current_url: pageView.url,
      title: pageView.title,
      $referrer: pageView.referrer,
      duration: pageView.duration,
    });
  }

  /**
   * Associates the current browser session with a user identifier and traits.
   *
   * @param userId Stable application user identifier.
   * @param traits Optional user traits forwarded to PostHog.
   * @returns Nothing.
   */
  async identify(userId: string, traits?: UserTraits): Promise<void> {
    const client = await this.getClient();
    client.identify(userId, traits);
  }

  /**
   * Updates the current PostHog profile traits for an already identified user.
   *
   * @param userId Stable application user identifier.
   * @param traits User traits forwarded to PostHog.
   * @returns Nothing.
   */
  async setUserProperties(userId: string, traits: UserTraits): Promise<void> {
    const client = await this.getClient();
    client.identify(userId, traits);
  }

  /**
   * Resets the PostHog client identity and local state.
   *
   * @returns Nothing.
   */
  async reset(): Promise<void> {
    const client = await this.getClient();
    client.reset();
  }

  /**
   * Checks whether a PostHog feature flag is enabled for the current subject.
   *
   * @param flagKey Feature flag key to resolve.
   * @returns `true` when PostHog reports the flag as enabled.
   */
  async isFeatureEnabled(flagKey: string): Promise<boolean> {
    const client = await this.getClient();
    return client.isFeatureEnabled(flagKey) ?? false;
  }

  /**
   * Returns the current PostHog feature flag payload for the supplied key.
   *
   * @param flagKey Feature flag key to resolve.
   * @returns The feature flag payload when PostHog has one for the current subject.
   */
  async getFeatureFlag(flagKey: string): Promise<string | boolean | undefined> {
    const client = await this.getClient();
    return client.getFeatureFlag(flagKey);
  }

  /**
   * Registers a callback that runs whenever PostHog refreshes feature flags.
   *
   * @param callback Callback invoked with the list of active feature flags.
   * @returns Nothing.
   */
  async onFeatureFlags(callback: (flags: string[]) => void): Promise<void> {
    const client = await this.getClient();
    client.onFeatureFlags(callback);
  }
}
